/**
 * TourSpotlight — full-viewport dim layer with a rectangular cut-out around
 * a measured target rect.
 *
 * Rendering approach: a single fixed-position div that's transparent itself
 * and casts a massive box-shadow (`0 0 0 9999px rgba(0,0,0,0.62)`). That
 * shadow IS the dim. Moving the div repositions the cut-out in real
 * viewport pixels — no SVG sizing dependencies, no mask-id collisions,
 * always works. Replaces the earlier SVG mask approach which the prototype
 * proved was broken in some browsers (the inner mask rect sits in a 300×150
 * default coordinate space when the SVG has no explicit viewBox).
 *
 * Animation: position + size transition over 320ms cubic-bezier. Suppressed
 * under `prefers-reduced-motion: reduce`.
 *
 * The dim layer is `pointer-events: none` so the spotlit element remains
 * clickable. Radix's Dialog (in <GuidedTour>) handles focus trap + Esc.
 */
import type { TourRect } from './types';

export interface TourSpotlightProps {
  readonly rect: TourRect | null;
  readonly padding: number;
  readonly reducedMotion: boolean;
  /** Border radius applied to the cut-out, in CSS pixels. */
  readonly radius?: number;
}

export default function TourSpotlight({
  rect,
  padding,
  reducedMotion,
  radius = 12,
}: TourSpotlightProps): React.ReactElement {
  // When we have no rect yet, render a zero-sized spotlight off-screen so
  // the dim still covers the viewport (everything is dimmed) and the
  // tooltip won't fight a half-rendered cut-out.
  const x = rect ? rect.left - padding : -9999;
  const y = rect ? rect.top - padding : -9999;
  const w = rect ? rect.width + padding * 2 : 0;
  const h = rect ? rect.height + padding * 2 : 0;

  const transition = reducedMotion
    ? 'none'
    : 'left 320ms cubic-bezier(0.2, 0.8, 0.2, 1), top 320ms cubic-bezier(0.2, 0.8, 0.2, 1), width 320ms cubic-bezier(0.2, 0.8, 0.2, 1), height 320ms cubic-bezier(0.2, 0.8, 0.2, 1)';

  return (
    <div
      data-testid="tour-spotlight"
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        width: w,
        height: h,
        borderRadius: radius,
        pointerEvents: 'none',
        zIndex: 70,
        // The accent ring traces the spotlit element. The 9999px shadow
        // is the dim that fills everything outside the box.
        boxShadow:
          '0 0 0 1px color-mix(in srgb, var(--wayne-accent) 60%, transparent), 0 0 0 9999px rgba(0, 0, 0, 0.62)',
        transition,
      }}
    />
  );
}
